import { useState } from "react";
import { MessageSquareOff, Loader2, Copy, Check, RotateCcw, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AdUnit from "../components/AdUnit";
import axios from "axios";

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api`;

const MEETING_TYPES = ["Team Standup", "Sprint Planning", "All-Hands", "1-on-1 with Manager", "Client Call", "Strategy Meeting", "Training Session", "Town Hall", "Status Update", "Brainstorming", "Retrospective", "Weekly Sync", "Board Meeting", "Lunch & Learn"];
const TONES_LIST = [
  { value: "casual", label: "Casual (Slack vibe)" },
  { value: "professional", label: "Professional (Email)" },
  { value: "brief", label: "Brief & Direct (2 lines max)" },
  { value: "apologetic", label: "Apologetic (for important ones)" },
  { value: "funny", label: "Funny (deadpan humor)" },
];

export default function ExcuseGenerator() {
  const [meetingType, setMeetingType] = useState("Team Standup");
  const [tone, setTone] = useState("professional");
  const [context, setContext] = useState("");
  const [excuse, setExcuse] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError("");
    setExcuse("");

    try {
      const res = await axios.post(`${API}/excuse/generate`, {
        meeting_type: meetingType,
        tone,
        context: context.trim(),
      });
      setExcuse(res.data.excuse);
    } catch (err) {
      setError("Failed to generate excuse. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(excuse);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      <section className="py-12 md:py-16 bg-[#F8FAFC] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#F0F9FF] flex items-center justify-center">
              <MessageSquareOff className="w-5 h-5 text-[#0284C7]" />
            </div>
            <span className="label-mono">Excuse Generator</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="excuse-page-title">
            Meeting excuse generator
          </h1>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl">
            Generate a polite, professional excuse to skip that meeting. Copy and send in one click.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-5">
            <div>
              <label className="label-mono block mb-2">Meeting Type</label>
              <Select value={meetingType} onValueChange={setMeetingType}>
                <SelectTrigger className="bg-slate-50 border-transparent focus:border-[#0284C7] h-11 rounded-md" data-testid="meeting-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEETING_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="label-mono block mb-2">Tone</label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="bg-slate-50 border-transparent focus:border-[#0284C7] h-11 rounded-md" data-testid="excuse-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONES_LIST.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="label-mono block mb-2">Context (optional)</label>
              <Input
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g. I already skipped last week's, boss is strict..."
                className="bg-slate-50 border-transparent focus:border-[#0284C7] h-11 rounded-md"
                data-testid="excuse-context-input"
              />
              <p className="text-xs text-slate-400 mt-1.5">Add context for a more tailored excuse</p>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleGenerate} disabled={loading} className="bg-[#0284C7] hover:bg-[#0369A1] text-white rounded-md px-6 h-11 font-medium btn-press" data-testid="generate-excuse-button">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><MessageSquareOff className="w-4 h-4 mr-2" />Generate Excuse</>}
              </Button>
              {excuse && (
                <Button variant="outline" onClick={handleGenerate} className="rounded-md h-11 px-4 border-slate-300 text-slate-600 btn-press" data-testid="regenerate-excuse-button">
                  <RefreshCw className="w-4 h-4 mr-1.5" />Another
                </Button>
              )}
            </div>
            {error && <p className="text-sm text-red-500" data-testid="excuse-error">{error}</p>}

            <AdUnit format="vertical" />
          </div>

          <div className="lg:col-span-7">
            <div className="border border-slate-200 rounded-lg bg-white min-h-[300px]">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <span className="label-mono">Your Excuse</span>
                {excuse && (
                  <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0284C7] transition-colors" data-testid="copy-excuse-button">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                )}
              </div>
              <div className="p-5" data-testid="excuse-output">
                {loading && (
                  <div className="flex items-center gap-3 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Crafting a believable excuse...</span>
                  </div>
                )}
                {!loading && !excuse && (
                  <div className="text-sm text-slate-400 flex flex-col items-center justify-center py-16">
                    <MessageSquareOff className="w-10 h-10 text-slate-300 mb-3" />
                    <p>Click "Generate Excuse" to get started</p>
                    <p className="text-xs text-slate-300 mt-1">No judgment here</p>
                  </div>
                )}
                {excuse && (
                  <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 p-4 rounded-md border border-slate-100">
                    {excuse}
                  </div>
                )}
              </div>
            </div>

            {/* Tips */}
            {excuse && (
              <div className="mt-4 p-4 border border-dashed border-slate-300 rounded-lg">
                <span className="label-mono block mb-2">Pro Tips</span>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>- Send 15-30 min before, not at the exact start time</li>
                  <li>- Never use the same excuse twice in a month</li>
                  <li>- Offer to review notes or follow up async — shows you care</li>
                  <li>- Hit "Another" to get a completely different excuse each time</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
